import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { Layout } from './layouts/layout.js';
import { AlprReviewScreen } from './screens/alpr-review.js';
import { AssistantScreen } from './screens/assistant.js';
import { AuditScreen } from './screens/audit.js';
import { ClientDetailScreen } from './screens/client-detail.js';
import { ClientsScreen } from './screens/clients.js';
import { MetricsScreen } from './screens/metrics.js';
import { OverviewScreen } from './screens/overview.js';
import { PricingSettingsScreen } from './screens/pricing-settings.js';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: Layout,
});

const overviewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  component: OverviewScreen,
});

const clientsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/clients',
  component: ClientsScreen,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/clients/$lotId',
  component: ClientDetailScreen,
});

const metricsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/metrics',
  component: MetricsScreen,
});

const pricingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/pricing',
  component: PricingSettingsScreen,
});

const auditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/audit',
  component: AuditScreen,
});

const alprRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/alpr/review',
  component: AlprReviewScreen,
});

const assistantRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/assistant',
  component: AssistantScreen,
});

const routeTree = rootRoute.addChildren([
  layoutRoute.addChildren([
    overviewRoute,
    clientsRoute,
    clientDetailRoute,
    metricsRoute,
    pricingRoute,
    auditRoute,
    alprRoute,
    assistantRoute,
  ]),
]);

export const buildRouter = (queryClient: QueryClient) =>
  createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });

export type AppRouter = ReturnType<typeof buildRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
